/**
 * Meeting Agenda & Tracker Page
 *
 * Weekly meeting tracking for CyberSweep team.
 * Four tabs: Weekly Assignments, Meeting Agenda, Meeting Minutes, Directory.
 */

import React, { useState } from 'react';
import { ChevronLeft, Calendar, Users, ClipboardList, BookOpen, FileText } from 'lucide-react';
import DirectoryTab from '../../components/cybersweep/DirectoryTab';
import WeeklyAssignmentsTab from '../../components/cybersweep/WeeklyAssignmentsTab';
import MeetingAgendaTab from '../../components/cybersweep/MeetingAgendaTab';
import MeetingMinutesTab from '../../components/cybersweep/MeetingMinutesTab';

interface MeetingAgendaTrackerProps {
  onBack: () => void;
}

type TabType = 'assignments' | 'agenda' | 'minutes' | 'directory';

// Get Monday date from week string (YYYY-Www format)
const getWeekStartDate = (weekString: string): string => {
  const [year, week] = weekString.split('-W').map(Number);
  const jan1 = new Date(year, 0, 1);
  const daysToMonday = (8 - jan1.getDay()) % 7;
  const firstMonday = new Date(jan1);
  firstMonday.setDate(jan1.getDate() + daysToMonday);
  const targetMonday = new Date(firstMonday);
  targetMonday.setDate(firstMonday.getDate() + (week - 1) * 7);
  return targetMonday.toISOString().split('T')[0];
};

const MeetingAgendaTracker: React.FC<MeetingAgendaTrackerProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<TabType>('assignments');

  // Get current week in YYYY-Www format
  const getCurrentWeek = () => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const days = Math.floor((now.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
    const weekNumber = Math.ceil((days + startOfYear.getDay() + 1) / 7);
    return `${now.getFullYear()}-W${String(weekNumber).padStart(2, '0')}`;
  };

  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());
  const weekStartDate = getWeekStartDate(selectedWeek);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div>
        {/* Back button */}
        <button
          onClick={onBack}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 mb-2"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Reports
        </button>

        {/* Title + Week Selector */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded">
              <Calendar className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                CyberSweep Reports
              </span>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Meeting Agenda & Tracker
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Week:</label>
            <input
              type="week"
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('assignments')}
            className={`py-1.5 px-1 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
              activeTab === 'assignments'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <ClipboardList className="h-4 w-4" />
            Weekly Assignments
          </button>
          <button
            onClick={() => setActiveTab('agenda')}
            className={`py-1.5 px-1 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
              activeTab === 'agenda'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <BookOpen className="h-4 w-4" />
            Meeting Agenda
          </button>
          <button
            onClick={() => setActiveTab('minutes')}
            className={`py-1.5 px-1 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
              activeTab === 'minutes'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <FileText className="h-4 w-4" />
            Meeting Minutes
          </button>
          <button
            onClick={() => setActiveTab('directory')}
            className={`py-1.5 px-1 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
              activeTab === 'directory'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <Users className="h-4 w-4" />
            Directory
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'assignments' && (
        <WeeklyAssignmentsTab selectedWeek={selectedWeek} />
      )}

      {activeTab === 'agenda' && (
        <MeetingAgendaTab selectedWeek={selectedWeek} weekStartDate={weekStartDate} />
      )}

      {activeTab === 'minutes' && (
        <MeetingMinutesTab selectedWeek={selectedWeek} weekStartDate={weekStartDate} />
      )}

      {activeTab === 'directory' && <DirectoryTab />}
    </div>
  );
};

export default MeetingAgendaTracker;
